import { useEffect, useMemo, useState } from "react";
import { Clock, Save, Send, X } from "lucide-react";
import { RichTextEditor } from "@/features/compose/RichTextEditor";
import { ComposeAttachments } from "@/features/compose/ComposeAttachments";
import { ComposeFileDropZone } from "@/features/compose/ComposeFileDropZone";
import { ComposeSignaturePreview } from "@/features/compose/ComposeSignaturePreview";
import { RecipientInput } from "@/features/contacts/RecipientInput";
import { ScheduleDateTimeModal } from "@/features/shared/ScheduleDateTimeModal";
import { useModalTransition } from "@/hooks/useModalTransition";
import {
  assembleInlineReplyOutbound,
  hasComposeSendableContent,
  htmlToPlainText,
  migrateComposeDraftBody,
  normalizeComposeOutboundBody,
  splitComposeBodyHtml,
} from "@/lib/compose/composeHtml";
import { readAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import { getAccountDisplayLabel } from "@/lib/mail/accountDisplayLabel";
import {
  attachmentsForApi,
  composeAttachmentsLoading,
  filesToComposeAttachments,
} from "@/lib/compose/composeAttachments";
import { composeRecipientsFieldValid } from "@/lib/compose/composeRecipients";
import type { OutboxEnqueuePayload } from "@/lib/outbox/outboxQueue";
import type { ComposeAttachment, ComposeDraft } from "@/lib/types/compose";
import type { MailAccountSummary } from "@/lib/types/mail";
import type { StoredComposeSession } from "@/lib/compose/composeSessionsStore";
import { clearComposeRecovery, writeComposeRecovery } from "@/lib/compose/composeLocalRecovery";
import { cn } from "@/lib/cn";

export const COMPOSE_INLINE_REPLY_TRANSITION_MS = 340;

type PanelProps = {
  session: StoredComposeSession;
  accounts?: MailAccountSummary[];
  accountEmail?: string;
  settingsVersion?: number;
  onUpdateDraft: (sessionId: string, patch: Partial<ComposeDraft>) => void;
  onSaveDraft: (sessionId: string, draft: ComposeDraft) => void;
  onDiscardDraft: (sessionId: string, draft: ComposeDraft) => void;
  onCloseSession: (sessionId: string) => void;
  onEnqueue: (sessionId: string, payload: OutboxEnqueuePayload) => void;
  onScheduleSend?: (sessionId: string, payload: OutboxEnqueuePayload, sendAt: string) => void;
  sending?: boolean;
};

const composeFieldClass =
  "w-full min-w-0 border-0 bg-transparent px-0 py-0 text-[0.8125rem] leading-snug text-[var(--mail-text)] placeholder:text-[var(--mail-muted)] focus:outline-none focus:ring-0 disabled:opacity-60";

const MODE_LABELS: Record<"reply" | "replyAll", string> = {
  reply: "Répondre",
  replyAll: "Répondre à tous",
};

function ComposeInlineReplyPanel({
  session,
  accounts = [],
  accountEmail,
  settingsVersion = 0,
  onUpdateDraft,
  onSaveDraft,
  onDiscardDraft,
  onCloseSession,
  onEnqueue,
  onScheduleSend,
  sending = false,
}: PanelProps) {
  const { id, draft, accountId } = session;
  const senderAccount = useMemo(
    () => accounts.find((entry) => entry.id === accountId),
    [accounts, accountId]
  );
  const senderLabel = senderAccount
    ? getAccountDisplayLabel(senderAccount)
    : accountEmail ?? "";
  const normalized = migrateComposeDraftBody(draft);
  const initialParts = splitComposeBodyHtml(normalized.bodyHtml);
  const accountSignature = useMemo(
    () => (accountId ? readAccountSettings(accountId).signature : ""),
    [accountId, settingsVersion]
  );
  const [to, setTo] = useState(draft.to);
  const [cc, setCc] = useState(draft.cc);
  const [subject, setSubject] = useState(draft.subject);
  const [bodyHtml, setBodyHtml] = useState(initialParts.messageHtml);
  const [attachments, setAttachments] = useState<ComposeAttachment[]>(draft.attachments ?? []);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [showCc, setShowCc] = useState(Boolean(draft.cc) || draft.mode === "replyAll");
  const [scheduleOpen, setScheduleOpen] = useState(false);

  useEffect(() => {
    const next = migrateComposeDraftBody(draft);
    const parts = splitComposeBodyHtml(next.bodyHtml);
    setTo(draft.to);
    setCc(draft.cc);
    setSubject(draft.subject);
    setBodyHtml(parts.messageHtml);
    setAttachments(draft.attachments ?? []);
    setAttachmentError(null);
    setShowCc(Boolean(draft.cc) || draft.mode === "replyAll");
  }, [draft, id]);

  function sync(patch: Partial<ComposeDraft>) {
    onUpdateDraft(id, patch);
    const next = { ...currentDraft(), ...patch };
    writeComposeRecovery({
      to: next.to,
      cc: next.cc,
      subject: next.subject,
      bodyHtml: next.bodyHtml,
    });
  }

  function currentDraft(): ComposeDraft {
    return {
      ...draft,
      to,
      cc,
      subject,
      bodyHtml,
      bodyText: htmlToPlainText(bodyHtml),
      attachments,
    };
  }

  function outboundDraft() {
    return assembleInlineReplyOutbound(currentDraft(), accountSignature);
  }

  async function handleAddAttachments(files: FileList | File[]) {
    const list = files instanceof FileList ? [...files] : files;
    if (list.length === 0) return;
    const result = await filesToComposeAttachments(list, attachments);
    setAttachments(result.attachments);
    setAttachmentError(result.error ?? null);
    sync({ attachments: result.attachments });
  }

  function handleRemoveAttachment(fileId: string) {
    const next = attachments.filter((file) => file.id !== fileId);
    setAttachments(next);
    setAttachmentError(null);
    sync({ attachments: next });
  }

  function handleBodyChange(html: string) {
    setBodyHtml(html);
    sync({
      bodyHtml: html,
      bodyText: htmlToPlainText(html),
    });
  }

  function handleSaveDraft() {
    clearComposeRecovery();
    const assembled = outboundDraft();
    onSaveDraft(id, {
      ...currentDraft(),
      bodyHtml: assembled.bodyHtml,
      bodyText: assembled.bodyText,
    });
  }

  function handleDiscardDraft() {
    clearComposeRecovery();
    onDiscardDraft(id, currentDraft());
  }

  function closeKeepingWork() {
    const assembled = outboundDraft();
    const keep = Boolean(
      to.trim() || cc.trim() || subject.trim() || assembled.bodyText.trim() || attachments.length
    );
    if (!keep) {
      clearComposeRecovery();
      onCloseSession(id);
      return;
    }
    if (!canSave) return;
    handleSaveDraft();
  }

  function buildOutboundPayload(): OutboxEnqueuePayload | null {
    if (!accountId || !composeRecipientsFieldValid(to)) return null;
    const assembled = outboundDraft();
    const { bodyHtml: outHtml, bodyText: outText } = normalizeComposeOutboundBody(
      assembled.bodyHtml,
      assembled.bodyText
    );

    return {
      accountId,
      fromEmail: senderAccount?.email ?? accountEmail,
      to: to.trim(),
      cc: cc.trim() || undefined,
      subject,
      bodyText: outText,
      bodyHtml: outHtml,
      attachments: attachmentsForApi(attachments),
      inReplyTo: draft.inReplyTo,
      references: draft.references,
    };
  }

  function handleSend() {
    if (sending) return;
    const payload = buildOutboundPayload();
    if (!payload) return;
    clearComposeRecovery();
    onEnqueue(id, payload);
  }

  function handleConfirmSchedule(untilIso: string) {
    const payload = buildOutboundPayload();
    if (!payload || !onScheduleSend) return;
    clearComposeRecovery();
    onScheduleSend(id, payload, untilIso);
    setScheduleOpen(false);
  }

  const attachmentsBusy = composeAttachmentsLoading(attachments);
  const assembledPreview = outboundDraft();
  const hasContent = hasComposeSendableContent({
    subject,
    bodyHtml: assembledPreview.bodyHtml,
    bodyText: assembledPreview.bodyText,
    attachments,
  });
  const canSend = !sending && composeRecipientsFieldValid(to) && hasContent && !attachmentsBusy;
  const canSchedule = canSend && onScheduleSend != null;
  const canSave = !sending && !attachmentsBusy;
  const title =
    draft.mode === "reply" || draft.mode === "replyAll"
      ? MODE_LABELS[draft.mode]
      : "Répondre";

  return (
    <ComposeFileDropZone
      className="compose-inline-reply shrink-0"
      disabled={attachmentsBusy}
      onAddFiles={handleAddAttachments}
    >
      <section aria-label={title}>
      <div className="compose-inline-reply__header">
        <p className="text-sm font-semibold text-[var(--mail-text)]">{title}</p>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={!canSave}
            className="compose-drawer-icon-btn"
            aria-label="Enregistrer en brouillon"
          >
            <Save className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={closeKeepingWork}
            className="compose-drawer-icon-btn"
            aria-label="Fermer"
            disabled={sending}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="compose-inline-reply__fields">
        {senderLabel ? (
          <div className="compose-field-row compose-field-row--readonly">
            <span className="compose-field-label">De</span>
            <span className="compose-field-value truncate">{senderLabel}</span>
          </div>
        ) : null}
        <div className="compose-field-row">
          <span className="compose-field-label">À</span>
          <RecipientInput
            label="À"
            value={to}
            placeholder="destinataire@domaine.fr"
            className={composeFieldClass}
            onChange={(next) => {
              setTo(next);
              sync({ to: next });
            }}
          />
          {!showCc && draft.mode !== "replyAll" ? (
            <button
              type="button"
              onClick={() => setShowCc(true)}
              className="compose-field-action"
            >
              Cc
            </button>
          ) : null}
        </div>

        {showCc || draft.mode === "replyAll" ? (
          <div className="compose-field-row">
            <span className="compose-field-label">Cc</span>
            <RecipientInput
              label="Cc"
              value={cc}
              placeholder="copie@domaine.fr"
              className={composeFieldClass}
              onChange={(next) => {
                setCc(next);
                sync({ cc: next });
              }}
            />
          </div>
        ) : null}

        <div className="compose-field-row">
          <span className="compose-field-label">Objet</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              sync({ subject: e.target.value });
            }}
            placeholder="Objet du message"
            className={composeFieldClass}
          />
        </div>
      </div>

      <div className="compose-inline-reply__editor">
        <RichTextEditor
          value={bodyHtml}
          onChange={handleBodyChange}
          placeholder="Écrivez votre message…"
          className="compose-inline-reply__rich-editor min-h-[8rem]"
          onFilesDropped={handleAddAttachments}
        />
        <ComposeSignaturePreview signature={accountSignature} />
      </div>

      <ComposeAttachments
        attachments={attachments}
        onAddFiles={handleAddAttachments}
        onRemove={handleRemoveAttachment}
        error={attachmentError}
      />

      <div className="compose-inline-reply__footer">
        <button
          type="button"
          onClick={handleDiscardDraft}
          className="compose-drawer-btn-secondary mail-btn-secondary-gradient"
          disabled={sending}
        >
          Annuler
        </button>
        <div className="compose-drawer-send-split">
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend}
            className="compose-drawer-btn-primary compose-drawer-btn-split-part"
          >
            <Send className="h-4 w-4" />
            {sending ? "Transmission…" : "Envoyer"}
          </button>
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            disabled={!canSchedule}
            className="compose-drawer-btn-primary compose-drawer-btn-split-part compose-drawer-btn-split-right"
            aria-label="Programmer l'envoi"
          >
            <Clock className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="compose-inline-reply__separator" aria-hidden />

      <ScheduleDateTimeModal
        open={scheduleOpen}
        title="Programmer l'envoi"
        subject={subject.trim() || undefined}
        description="Choisissez quand envoyer ce message"
        confirmLabel="Programmer"
        previewPrefix="Envoi le"
        overlayBackdrop="transparent"
        onConfirm={handleConfirmSchedule}
        onClose={() => setScheduleOpen(false)}
      />
      </section>
    </ComposeFileDropZone>
  );
}

type SlotProps = Omit<PanelProps, "session"> & {
  open: boolean;
  session: StoredComposeSession | null;
};

/** Bloc réponse inline avec animation ouverture (haut→bas) / fermeture (bas→haut). */
export function ComposeInlineReplySlot({
  open,
  session,
  ...panelProps
}: SlotProps) {
  const { mounted, visible } = useModalTransition(open, COMPOSE_INLINE_REPLY_TRANSITION_MS);
  const [panelSession, setPanelSession] = useState(session);

  useEffect(() => {
    if (session) setPanelSession(session);
  }, [session]);

  if (!mounted || !panelSession) return null;

  return (
    <div
      className={cn(
        "compose-inline-reply-wrap shrink-0",
        visible && "compose-inline-reply-wrap--visible"
      )}
    >
      <div className="compose-inline-reply-inner">
        <ComposeInlineReplyPanel session={panelSession} {...panelProps} />
      </div>
    </div>
  );
}
